import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'
import type {
  CandidateDto, CandidateRefDto, CoalitionDto, CoalitionMembershipDto, PageResponse,
  PartyDto, SeatTypeDto,
} from '@/shared/api/types'

/**
 * Coalitions, parties and candidates.
 *
 * ## Deliberately small
 *
 * This is the minimum the Polling Studio needs to reference: who is standing,
 * for what seat, under whose banner. Nominations, party primaries, candidate
 * movements between parties, tally returns and manifesto tracking are NOT here
 * and are not stubbed — a stub is a promise, and an empty screen with no writer
 * behind it is worse than an honest absence.
 *
 * ## The one rule worth knowing
 *
 * A seat type determines the LEVEL of unit a candidate contests: PRESIDENT is
 * national, GOVERNOR/SENATOR/WOMAN_REP are county, MP is constituency, MCA is
 * ward. The server refuses a mismatch, and `/seat-types` publishes the mapping so
 * the unit picker can filter to the right level rather than letting somebody pick
 * a ward for a presidential candidate and discover it in a 400.
 *
 * ## The colour is data
 *
 * `colourHex` lives on the party and the coalition. Every chart that colours by
 * political entity reads it from here. PollSphere hardcoded a substring table —
 * `Sakaja→#16a34a`, `Sifuna→#dc2626` — duplicated verbatim in two files, so a
 * sixth candidate was grey and a renamed one changed colour.
 */

// ── parties ─────────────────────────────────────────────────────────────────

export interface PartyFilter {
  status?: string
  search?: string
}

export function listParties(filter: PartyFilter = {}, page = 0, size = 50) {
  return api.get<PageResponse<PartyDto>>(`/politics/parties${qs({ ...filter, page, size })}`)
}

export function getParty(id: string) {
  return api.get<PartyDto>(`/politics/parties/${id}`)
}

export function createParty(body: Record<string, unknown>) {
  return api.post<PartyDto>('/politics/parties', body)
}

export function updateParty(id: string, body: Record<string, unknown>) {
  return api.put<PartyDto>(`/politics/parties/${id}`, body)
}

/**
 * Join a coalition.
 *
 * A party has at most ONE open membership — a partial unique index enforces it,
 * so joining while already in one is a refusal rather than a silent second row.
 * Leave the current one first; the history keeps both.
 */
export function joinCoalition(partyId: string, body: {
  coalitionId: string; joinedOn: string; roleCode?: string
}) {
  return api.post<CoalitionMembershipDto>(`/politics/parties/${partyId}/coalition`, body)
}

export function leaveCoalition(partyId: string, leftOn?: string) {
  return api.del<CoalitionMembershipDto>(
    `/politics/parties/${partyId}/coalition${qs({ leftOn })}`)
}

/** Every membership this party has ever held, open and closed. */
export function coalitionHistory(partyId: string) {
  return api.get<CoalitionMembershipDto[]>(`/politics/parties/${partyId}/coalition-history`)
}

// ── coalitions ──────────────────────────────────────────────────────────────

export function listCoalitions(filter: PartyFilter = {}, page = 0, size = 50) {
  return api.get<PageResponse<CoalitionDto>>(
    `/politics/coalitions${qs({ ...filter, page, size })}`)
}

/** The detail read carries `members`; a list row carries only counts. */
export function getCoalition(id: string) {
  return api.get<CoalitionDto>(`/politics/coalitions/${id}`)
}

export function createCoalition(body: Record<string, unknown>) {
  return api.post<CoalitionDto>('/politics/coalitions', body)
}

export function updateCoalition(id: string, body: Record<string, unknown>) {
  return api.put<CoalitionDto>(`/politics/coalitions/${id}`, body)
}

// ── candidates ──────────────────────────────────────────────────────────────

export interface CandidateFilter {
  seatType?: string
  status?: string
  partyId?: string
  coalitionId?: string
  unitId?: string
  search?: string
}

/**
 * List candidates.
 *
 * The wire names are `seatType` and `status` — the response components are
 * `seatTypeCode` and `statusCode`. They do not match, and the controller builds
 * its filter positionally rather than binding it, so sending the `*Code` names
 * filters nothing and returns everything.
 */
export function listCandidates(filter: CandidateFilter = {}, page = 0, size = 50) {
  return api.get<PageResponse<CandidateDto>>(
    `/politics/candidates${qs({ ...filter, page, size })}`)
}

/** Typeahead for the poll builder's entity linker. Scope-filtered server-side. */
export function searchCandidates(q: string, seatType?: string, scopeUnitId?: string, limit = 20) {
  return api.get<CandidateRefDto[]>(
    `/politics/candidates/search${qs({ q, seatType, scopeUnitId, limit })}`)
}

export function getCandidate(id: string) {
  return api.get<CandidateDto>(`/politics/candidates/${id}`)
}

export function createCandidate(body: Record<string, unknown>) {
  return api.post<CandidateDto>('/politics/candidates', body)
}

export function updateCandidate(id: string, body: Record<string, unknown>) {
  return api.put<CandidateDto>(`/politics/candidates/${id}`, body)
}

export function deleteCandidate(id: string) {
  return api.del<void>(`/politics/candidates/${id}`)
}

// ── seat types ──────────────────────────────────────────────────────────────

/** `{code, requiredUnitLevel}` — the rule, published rather than reimplemented. */
export function listSeatTypes() {
  return api.get<SeatTypeDto[]>('/politics/seat-types')
}
